import { useState } from 'react';
import { BrandSymbol, Button, Container, Group, Text } from '@melio/penny';
import { SsoProvider } from '../../hooks/useSsoNavigation';

type SsoButtonsVariant = 'sign-in' | 'sign-up';

export interface SsoButtonsProps {
  variant: SsoButtonsVariant;
  onGoogleClick: () => void;
  onXeroClick: () => void;
  onIntuitClick: () => void;
  loadingProvider?: SsoProvider | null;
  // Disables all three regardless of provider — for when some other action on the
  // same page (e.g. the email/password form submit) is in flight instead.
  isDisabled?: boolean;
}

const testIdSuffix = (v: SsoButtonsVariant) => (v === 'sign-up' ? 'sign-up' : 'sign-in');

const INTUIT_BUTTON_STYLE = { borderColor: '#0077C7', color: '#0077C7' };
const INTUIT_HOVER_BG = '#F1F5F8';

export const SsoButtons = ({
  variant,
  onGoogleClick,
  onXeroClick,
  onIntuitClick,
  loadingProvider = null,
  isDisabled = false,
}: SsoButtonsProps) => {
  const [isIntuitHovered, setIsIntuitHovered] = useState(false);
  const suffix = testIdSuffix(variant);
  // Disable the other two while one provider's redirect is in flight, so a click
  // can't fire a second startSso() (and overwrite the in-flight PKCE verifier) before navigation happens.
  const isAnyLoading = loadingProvider !== null;
  const isIntuitDisabled = isDisabled || (isAnyLoading && loadingProvider !== 'intuit');
  // Only apply Intuit's custom brand color while enabled — otherwise it overrides Penny's
  // disabled-state styling and the button never looks grayed out.
  const intuitStyle = isIntuitDisabled
    ? undefined
    : {
        ...INTUIT_BUTTON_STYLE,
        ...(isIntuitHovered && { backgroundColor: INTUIT_HOVER_BG }),
      };

  return (
    <Group variant="vertical" spacing="s">
      <Button
        variant="tertiary"
        color="secondary"
        size="medium"
        isFullWidth
        label="Continue with Google"
        leftElement={<BrandSymbol type="google" size="medium" aria-label="Google" />}
        onClick={onGoogleClick}
        isLoading={loadingProvider === 'google'}
        isDisabled={isDisabled || (isAnyLoading && loadingProvider !== 'google')}
        data-testid={`google-${suffix}-button`}
      />

      <Button
        variant="tertiary"
        color="secondary"
        size="medium"
        isFullWidth
        label="Continue with Xero"
        leftElement={<BrandSymbol type="xero" size="medium" aria-label="Xero" />}
        onClick={onXeroClick}
        isLoading={loadingProvider === 'xero'}
        isDisabled={isDisabled || (isAnyLoading && loadingProvider !== 'xero')}
        data-testid={`xero-${suffix}-button`}
      />

      <Container textAlign="center">
        <Button
          variant="secondary"
          size="medium"
          isFullWidth
          label="Sign in with Intuit"
          onClick={onIntuitClick}
          onMouseEnter={() => setIsIntuitHovered(true)}
          onMouseLeave={() => setIsIntuitHovered(false)}
          style={intuitStyle}
          isLoading={loadingProvider === 'intuit'}
          isDisabled={isIntuitDisabled}
          data-testid={`intuit-${suffix}-button`}
        />
        <Text textStyle="body4" color="semantic.text.secondary">
          {variant === 'sign-up' ? 'Sign up with your' : 'Sign in with your'}{' '}
          <Text as="span" textStyle="body4Semi" color="semantic.text.secondary">
            QuickBooks Online
          </Text>{' '}
          account.
        </Text>
      </Container>
    </Group>
  );
};
